import { useMemo, useCallback } from "react";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { FilterOption } from "@/components/ui/table-header/types";

interface UseStoneTypeFiltersProps {
    stoneTypes: StoneType[];
    searchTerm: string;
    showArchived: boolean;
    nameFilters: string[];
}

export const useStoneTypeFilters = ({
    stoneTypes,
    searchTerm,
    showArchived,
    nameFilters,
}: UseStoneTypeFiltersProps) => {
    const filteredStoneTypes = useMemo(() => {
        if (!stoneTypes || stoneTypes.length === 0) return [];

        return stoneTypes.filter((stoneType) => {
            // Filter by archive status
            if (!showArchived && stoneType.isArchived) return false;

            // Filter by search term
            if (searchTerm && !stoneType.name?.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Filter by name filters
            if (nameFilters.length > 0 && !nameFilters.includes(stoneType.name)) {
                return false;
            }

            return true;
        });
    }, [stoneTypes, searchTerm, showArchived, nameFilters]);

    const getUniqueOptions = useCallback(
        (field: keyof StoneType): FilterOption[] => {
            if (!stoneTypes || stoneTypes.length === 0) return [];

            const uniqueValues = new Set<string>();

            stoneTypes.forEach((stoneType) => {
                if (field === "name" && stoneType.name) {
                    uniqueValues.add(stoneType.name);
                }
            });

            return Array.from(uniqueValues)
                .sort()
                .map((value) => ({
                    label: value,
                    value: value,
                }));
        },
        [stoneTypes]
    );

    return {
        filteredStoneTypes,
        getUniqueOptions,
    };
}; 